import { Button } from "@elit-hair/elit-ui";
import React, { useRef } from "react";
import { MdBackspace } from "react-icons/md";
import { twMerge } from "tailwind-merge";

import { soundService } from "@/services/soundService";

type DialButtonProps = {
    value: string;
    onClick: (value: string) => void;
    letters?: React.ReactNode;
};

export const DialButton = ({ value, onClick, letters }: DialButtonProps) => {
    const isBackspace = value === "<";
    const isPlus = value === "+";
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const handleClick = async () => {
        if (!isBackspace && !isPlus) {
            await soundService.playDTMF(value);
        }
        if (!isBackspace) {
            onClick(value);
        }
    };

    const handleBackspaceDown = () => {
        timerRef.current = setTimeout(() => {
            onClick("clear"); // Clear all after 1s
        }, 1000);
    };

    const handleBackspaceUp = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
            onClick("<"); // Delete one character
        }
    };

    return (
        <Button
            variant="outline"
            size="icon"
            className={twMerge(
                "w-full h-[8vh] max-h-16 rounded-sm flex flex-col items-center justify-center text-xl font-semibold bg-secondary backdrop-blur border border-secondary shadow-sm transition-all",
                isBackspace ? "text-flat-primary" : "text-flat-gray-900",
                "hover:bg-flat-gray-50 active:bg-flat-gray-100 hover:border-flat-gray-300",
                isPlus && "text-flat-primary font-bold"
            )}
            onClick={isBackspace ? undefined : handleClick}
            onMouseDown={isBackspace ? handleBackspaceDown : undefined}
            onMouseUp={isBackspace ? handleBackspaceUp : undefined}
            onMouseLeave={isBackspace ? handleBackspaceUp : undefined}
            onTouchStart={isBackspace ? handleBackspaceDown : undefined}
            onTouchEnd={isBackspace ? handleBackspaceUp : undefined}
        >
            {isBackspace ? <MdBackspace size={18} /> : value}
            {letters && !isBackspace && !isPlus && (
                <span className="text-xs text-gray-400 font-normal -mt-1">{letters}</span>
            )}
        </Button>
    );
};
